import { Flex, Heading, Link, Text } from "@chakra-ui/layout";
import { Button, Tooltip } from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/react-table";
import React from "react";
import { RiSettings5Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { OrganizationDateView } from "src/components/DataDisplay/OrganizationDateView";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { RouterLink } from "src/components/Links/RouterLink";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { useEnrollmentPeriod } from "src/components/Providers/EnrollmentPeriodProvider";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { CustomColumnsTable } from "src/components/Table/CustomColumnsTable";
import {
  useOrderByParams,
  usePaginationParams,
} from "src/hooks/useCommonSearchParams";
import {
  FormServiceMatchRunListItem,
  useFormServiceMatchRunsList,
} from "src/hooks/useFormServiceMatch";
import { useOrganization } from "src/hooks/useOrganization";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import * as breadcrumb from "src/services/breadcrumb";
import * as format from "src/services/format";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { EmptyMatchConfig } from "./components/EmptyMatchConfig";

function toAdminPerson(person: {
  fullName: string | null;
  emailAddress: string | null;
}) {
  return {
    full_name: person.fullName,
    email_address: person.emailAddress,
  };
}

export const MatchIndexFormService = (): React.ReactElement => {
  const organization = useOrganization();
  const { selectedEnrollmentPeriod } = useEnrollmentPeriod();
  const navigate = useNavigate();
  const flags = useFlags(["lottery-position"]);
  const { orderBy, setOrderBy } = useOrderByParams({
    defaultOrderBy: { sortKey: "created_at", sortType: GQL.order_by.desc },
  });

  const { isElevatedSchoolAdmin, applyingSchoolIds, attendingSchoolIds } =
    useSchoolAdmin();

  const { pagination, setPagination } = usePaginationParams();
  const schoolIds = isElevatedSchoolAdmin
    ? [...applyingSchoolIds, ...attendingSchoolIds]
    : undefined;

  const { remoteData } = useFormServiceMatchRunsList({
    enrollmentPeriodId: selectedEnrollmentPeriod?.id ?? "",
    limit: pagination.limit,
    offset: pagination.offset,
    sortKey: "created_at",
    sortType: orderBy?.sortType === GQL.order_by.asc ? "asc" : "desc",
    schoolIds,
    enabled: Boolean(selectedEnrollmentPeriod?.id) && organization.hasData(),
  });

  return (
    <RemoteDataView remoteData={remoteData} error={() => <GenericError />}>
      {({ matchRuns, totalCount }) => {
        const columns: ColumnDef<FormServiceMatchRunListItem>[] = [
          {
            id: "name",
            header: "Match name",
            cell: (props) => (
              <Link
                as={RouterLink}
                color="primary.500"
                to={organization
                  .map((org) =>
                    Url.OrgAdmin.Match.details(org, props.row.original.id)
                  )
                  .withDefault("#")}
                textDecoration="underline"
              >
                {props.row.original.name ?? "Match"}
              </Link>
            ),
          },
          {
            id: "created_at",
            header: "Date run",
            cell: (props) => (
              <OrganizationDateView isoDate={props.row.original.createdAt} />
            ),
          },
          {
            id: "status",
            header: "Status",
            cell: (props) => (
              <Tooltip label={props.row.original.statusMessage ?? ""}>
                <Text cursor="default">{props.row.original.status}</Text>
              </Tooltip>
            ),
          },
          {
            id: "owner",
            header: "Run by",
            cell: (props) =>
              format.adminName(
                props.row.original.owner
                  ? toAdminPerson(props.row.original.owner)
                  : null,
                "Avela admin"
              ),
          },
          ...((flags["lottery-position"].enabled
            ? [
                {
                  id: "publishedAt",
                  header: "Position last saved",
                  cell: (props) => (
                    <OrganizationDateView
                      isoDate={
                        props.row.original.latestPublishEvent?.publishedAt
                      }
                    />
                  ),
                },
                {
                  id: "publishedBy",
                  header: "Position saved by",
                  cell: (props) => {
                    const publishEvent = props.row.original.latestPublishEvent;
                    if (!publishEvent) return null;
                    return format.adminName(
                      toAdminPerson(publishEvent.publishedBy),
                      "Avela admin"
                    );
                  },
                },
              ]
            : []) satisfies ColumnDef<FormServiceMatchRunListItem>[]),
        ];

        return (
          <Flex direction="column">
            <Breadcrumb
              items={breadcrumb.match.getBreadcrumbsForList(organization)}
              mb={4}
            />
            <Flex justifyContent="space-between" alignItems="center">
              <Heading as="h1" variant="admin">
                Match
              </Heading>
              <WithUserPermissions
                permissions={[
                  "match.configuration:create",
                  "match.configuration:read",
                ]}
              >
                <Button
                  leftIcon={<RiSettings5Line />}
                  onClick={() =>
                    navigate(
                      organization
                        .map((org) => Url.OrgAdmin.Match.configure(org))
                        .withDefault("#")
                    )
                  }
                >
                  Match rules
                </Button>
              </WithUserPermissions>
            </Flex>

            {matchRuns.length === 0 ? (
              <EmptyMatchConfig />
            ) : (
              <Flex mt={10}>
                <CustomColumnsTable<FormServiceMatchRunListItem>
                  columns={columns}
                  data={matchRuns}
                  limit={pagination.limit}
                  offset={pagination.offset}
                  count={totalCount}
                  sortableColumnIds={["created_at"]}
                  onFetchMore={setPagination}
                  onChangeSort={setOrderBy}
                />
              </Flex>
            )}
          </Flex>
        );
      }}
    </RemoteDataView>
  );
};
